import type { DateTime } from 'luxon';
import Alert from '../../../../components/alert/Alert';
import classnames from 'classnames';
import { useQuery } from 'react-query';
import { QUERIES_IDS } from '../../../../util/vars';
import { repository } from '@whizz/api-client';
import { renderMandrillTemplate } from '../../../../helper/handlebars';
import { useI18n } from '@whizz/react-i18n';

const WEEKLY_LEADERS_EMAIL_TEMPLATE = {
  'en-gb': 'weekly-leaders-en-gb',
  'en-us': 'weekly-leaders-en-us',
  'en-nz': 'weekly-leaders-en-nz',
  'es-mx': 'weekly-leaders-es-mx',
  'sw-ke': 'weekly-leaders-sw-ke',
  'th-th': 'weekly-leaders-th-th',
};

export default function EmailPreview({ previewData }: Props) {
  const { translate: t } = useI18n();

  const locale = previewData.school?.locale ?? 'en-gb';
  const template = WEEKLY_LEADERS_EMAIL_TEMPLATE[locale as keyof typeof WEEKLY_LEADERS_EMAIL_TEMPLATE] ?? WEEKLY_LEADERS_EMAIL_TEMPLATE['en-gb'];

  const getMailchimpRenderedTemplateQuery = useQuery({
    queryKey: [ QUERIES_IDS.ADMIN.GET_MAILCHIMP_RENDERED_TEMPLATE, { template, locale } ],
    queryFn: () => repository.AdminRepository.getMailchimpRenderedTemplate(template, { locale }),
  });

  const getWeeklyLeadersPreviewDynamicDataQuery = useQuery({
    queryKey: [
      QUERIES_IDS.ADMIN.GET_SCHOOL_WEEKLY_LEADERS_EMAIL_PREVIEW_DYNAMIC_DATA,
      {
        school: previewData.school?.id,
        startDate: previewData.startDate,
        endDate: previewData.endDate,
      },
    ],
    queryFn: () => {
      if (!previewData.school) {
        throw new Error('School is null');
      }
      return repository.AdminRepository.getSchoolWeeklyLeadersEmailPreviewDynamicData(
        previewData.school.id,
        previewData.startDate!,
        previewData.endDate!,
      );
    },
    enabled: previewData.school !== null && previewData.startDate !== null && previewData.endDate !== null,
  });

  return (
    <div className={'flex flex-col gap-5'}>
      <h2 className={'text-xl font-bold'}>
        {t('pages.ADMIN.PREVIEW.TITLE')}
      </h2>

      {!previewData.isEnabled && (
        <Alert show type={'warning'}>
          {t('pages.ADMIN.PREVIEW.DISABLED')}
        </Alert>
      )}

      <div className={classnames({ 'opacity-50': !previewData.isEnabled })}>
        {(getMailchimpRenderedTemplateQuery.isSuccess) && (
          <div
            dangerouslySetInnerHTML={{
              __html: renderMandrillTemplate(
                getMailchimpRenderedTemplateQuery.data.data,
                {
                  school: {
                    name: previewData.school?.name,
                  },
                  startDate: {
                    weekday: previewData.startDate?.setLocale(locale).toFormat('cccc'),
                    dayMonth: previewData.startDate?.setLocale(locale).toFormat('dd MMMM'),
                  },
                  endDate: {
                    weekday: previewData.endDate?.setLocale(locale).toFormat('cccc'),
                    dayMonth: previewData.endDate?.setLocale(locale).toFormat('dd MMMM'),
                  },
                  teachers: previewData.teachers.join(', '),
                  standardGreeting: previewData.standardGreeting,
                  message: previewData.message?.split('\n').join('<br />'),
                  reminderMessage: previewData.reminderMessage?.split('\n').join('<br />'),
                  metricsComponents: previewData.metricsComponents.map((metricsComponent) => ({
                    type: metricsComponent,
                    value: getWeeklyLeadersPreviewDynamicDataQuery.data?.data.stats.metricsComponents[metricsComponent].value ?? 0,
                    ...(metricsComponent === 'hall_of_fame' ? { total: getWeeklyLeadersPreviewDynamicDataQuery.data?.data.stats.metricsComponents[metricsComponent]?.total } : {}),
                    previousValue: getWeeklyLeadersPreviewDynamicDataQuery.data?.data.stats.metricsComponents[metricsComponent].previousValue ?? 0,
                  })),
                  classes: getWeeklyLeadersPreviewDynamicDataQuery.data?.data.stats.classes ?? [],
                },
              ),
            }}
          />
        )}
      </div>
    </div>
  );
}


interface Props {
  previewData: PreviewData;
}

export interface PreviewData {
  isEnabled: boolean;
  school: {
    id: number;
    name: string;
    locale: string;
  } | null;
  startDate: DateTime | null;
  endDate: DateTime | null;
  teachers: string[];
  standardGreeting: boolean;
  message: string | null;
  reminderMessage: string | null;
  metricsComponents: ('usage' | 'progressions' | 'hall_of_fame')[];
  studentsData: {
    fullName: string;
    assessed: number;
    unAssessed: number;
    usage: number;
    progressions: number;
    extraProgressions: number;
  }[];
}
